import _extends from "@babel/runtime/helpers/extends";
import React from 'react';
import { Tooltip } from '@parchment/atoms';
import { SCALE, VALIDATION_STATES } from '../../utils/constants';
import styled from 'styled-components';
const CONTEXT_MAP = {
  [VALIDATION_STATES.error]: 'invalid',
  [VALIDATION_STATES.success]: 'valid',
  [VALIDATION_STATES.neutral]: 'info',
  [VALIDATION_STATES.pending]: 'info'
};

/**
 * Encapsulates logic around what a tooltip should display for a validated field. For example:
 *   * When a field uses an internal label, the label should be displayed as the tooltip message
 *   * When a field uses an internal label and has a neutral validation state, the label should display in the "info" context.
 *   * When a field uses an internal label and has a success validation state, the label should display in the "valid" context.
 *   * When a field uses an external label, the tooltip should only display when there are error messages.
 *   * When a field uses any label type and has a error validation state, the label should display in the "error" context.
 *   * When a tooltip is displayed in an error state, the title should contain the field label and the message should be the errors.
 *   * When a tooltip is displayed in a non-error state, there should be NO title and the message should be the field label.
 *   * When a field is disabled, the tooltip should display in the "info" context regardless of validation state.
 *   * A tooltip should only display when the field is hovered or focused.
 */
export default function ValidatedFieldTooltip(_ref) {
  let {
    valid = VALIDATION_STATES.neutral,
    fieldDisabled = false,
    internalLabel = false,
    scale = SCALE.medium,
    label,
    error,
    placement,
    ...props
  } = _ref;
  let title = undefined;
  let message = internalLabel ? label : undefined;
  const context = fieldDisabled ? CONTEXT_MAP[VALIDATION_STATES.neutral] : CONTEXT_MAP[valid];
  if (valid === VALIDATION_STATES.error && !fieldDisabled) {
    title = label;
    message = error || 'Field is invalid';
  }
  return /*#__PURE__*/React.createElement(ParentRespectiveTooltip, _extends({
    title: title,
    message: message,
    context: context,
    internalLabel: internalLabel,
    scale: scale,
    placement: placement
  }, props));
}

/*  Necessary for parent respective block items, when wrapped with tooltips, to size to their parent.
        If the label is internal, the height is adjusted to 100% of its parent
        If external, the height is adjusted to 100% minus the label height (always 20px) + label bottom margin
*/
const ParentRespectiveTooltip = /*#__PURE__*/styled(Tooltip).withConfig({
  displayName: "ValidatedFieldTooltip__ParentRespectiveTooltip",
  componentId: "sc-1ui1js7-0"
})(["height:", ";"], props => props.internalLabel ? '100%' : `calc(100% - ${{
  default: '28px',
  small: '25px',
  medium: '28px',
  large: '30px'
}[props.scale]})`);